import { useCallback, useEffect, useState, type RefObject } from 'react'

export type ScrollOverflowEdges = {
  top: boolean
  right: boolean
  bottom: boolean
  left: boolean
}

const EMPTY_EDGES: ScrollOverflowEdges = {
  top: false,
  right: false,
  bottom: false,
  left: false,
}

const SCROLL_EPS = 1

export function useScrollOverflowEdges(
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): ScrollOverflowEdges {
  const [edges, setEdges] = useState<ScrollOverflowEdges>(EMPTY_EDGES)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) {
      setEdges(EMPTY_EDGES)
      return
    }
    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = el
    setEdges({
      top: scrollTop > SCROLL_EPS,
      bottom: scrollTop + clientHeight < scrollHeight - SCROLL_EPS,
      left: scrollLeft > SCROLL_EPS,
      right: scrollLeft + clientWidth < scrollWidth - SCROLL_EPS,
    })
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const scheduleUpdate = () => {
      update()
      requestAnimationFrame(update)
    }

    scheduleUpdate()
    const ro = new ResizeObserver(scheduleUpdate)
    ro.observe(el)
    const table = el.querySelector('table')
    if (table) ro.observe(table)
    // Watch only direct-child additions/removals and style changes on the container itself.
    // subtree:true + attributes:true would re-fire on every class toggle inside cards
    // (e.g. selection state), creating an infinite MutationObserver → setState → re-render loop.
    const mo = new MutationObserver(scheduleUpdate)
    mo.observe(el, { childList: true, subtree: false, attributes: true, attributeFilter: ['style'] })
    el.addEventListener('scroll', update, { passive: true })

    return () => {
      ro.disconnect()
      mo.disconnect()
      el.removeEventListener('scroll', update)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies content/layout deps
  }, [update, ...deps])

  return edges
}
